import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Flame, ArrowRight, Shield, BarChart3, Kanban, SearchCheck,
  Phone, Building2, Wrench
} from "lucide-react";

const features = [
  {
    icon: Kanban,
    title: "Pipeline Management",
    desc: "Track leads through every stage with drag-and-drop Kanban boards",
  },
  {
    icon: SearchCheck,
    title: "Lead Finder",
    desc: "Scrape local businesses from Google Places and import directly as leads",
  },
  {
    icon: BarChart3,
    title: "Dashboard Analytics",
    desc: "Monitor pipeline value, conversion rates, and revenue at a glance",
  },
  {
    icon: Phone,
    title: "Quick Actions",
    desc: "One-tap phone dial and email from any lead card on mobile or desktop",
  },
  {
    icon: Shield,
    title: "Secure Access",
    desc: "Google authentication with email whitelist for authorised users only",
  },
  {
    icon: Wrench,
    title: "Gas Engineering Focus",
    desc: "Built for boiler installs, safety inspections, heating systems, and more",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();

  const handleSignIn = () => navigate("/login");

  return (
    <div className="min-h-screen bg-background overflow-x-hidden" data-testid="landing-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-blue-500 flex items-center justify-center">
              <Flame className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight uppercase text-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              GASFLOW PRO
            </span>
          </div>
          <Button
            data-testid="landing-sign-in-nav"
            onClick={handleSignIn}
            variant="outline"
            className="rounded-sm border-white/10 text-xs uppercase tracking-wider hover:bg-white/5"
          >
            Sign In
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-6">
              <div className="h-px w-8 bg-blue-500" />
              <span className="text-xs font-bold tracking-[0.3em] uppercase text-blue-400">
                Commercial Gas Engineering
              </span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight uppercase text-foreground leading-[1.1]"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Manage Your
              <br />
              <span className="text-blue-400">Gas Engineering</span>
              <br />
              Leads In One Place
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-6 max-w-xl leading-relaxed">
              Purpose-built CRM for commercial gas engineers. Track leads from first contact to completed job,
              find new business with Google Places, and never miss a follow-up.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-10">
              <Button
                data-testid="landing-get-started"
                onClick={handleSignIn}
                className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-sm h-12 px-8"
              >
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-muted-foreground">
                Sign in with Google &middot; Authorised access only
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 sm:gap-8 mt-16 sm:mt-20 max-w-lg">
            {[
              { val: "7", label: "Pipeline Stages" },
              { val: "16+", label: "Business Categories" },
              { val: "8", label: "Service Types" },
            ].map((stat) => (
              <div key={stat.label} className="text-center sm:text-left">
                <p
                  className="text-3xl sm:text-4xl font-bold text-blue-400"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {stat.val}
                </p>
                <p className="text-xs text-muted-foreground mt-1 tracking-wider uppercase">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* Features */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-4">
              <div className="h-px w-8 bg-amber-500" />
              <span className="text-xs font-bold tracking-[0.3em] uppercase text-amber-400">
                Features
              </span>
            </div>
            <h2
              className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-foreground"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Everything You Need to Grow
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {features.map((f, i) => (
              <div
                key={f.title}
                className="group p-6 rounded-sm border border-white/5 bg-card hover:bg-white/[0.03] hover:border-white/10 transition-all duration-200 animate-fade-slide-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 rounded-sm bg-blue-500/10 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors duration-200">
                  <f.icon className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                </div>
                <h3
                  className="text-base font-bold tracking-tight uppercase text-foreground mb-2"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                >
                  {f.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      {/* CTA */}
      <section className="py-16 sm:py-24 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="w-14 h-14 rounded-sm bg-blue-500 flex items-center justify-center mx-auto mb-6">
            <Building2 className="w-7 h-7 text-white" />
          </div>
          <h2
            className="text-2xl sm:text-3xl font-bold tracking-tight uppercase text-foreground mb-4"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Ready to Streamline Your Pipeline?
          </h2>
          <p className="text-base text-muted-foreground mb-8 max-w-md mx-auto">
            Stop losing leads. Start closing more commercial gas jobs.
          </p>
          <Button
            data-testid="landing-cta-sign-in"
            onClick={handleSignIn}
            className="rounded-sm bg-blue-500 hover:bg-blue-600 text-white uppercase tracking-wider text-sm h-12 px-10"
          >
            Sign In with Google <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-sm bg-blue-500 flex items-center justify-center">
              <Flame className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-xs font-bold tracking-tight uppercase text-muted-foreground" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              GASFLOW PRO
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground/50 tracking-wider uppercase">
            Commercial Gas Engineering CRM &middot; Authorised Access Only
          </p>
        </div>
      </footer>
    </div>
  );
}
